import { FunctionComponent, useMemo } from "react";
import "./TOSHeader.css"
import { useStoreActions, useStoreState } from "../../store";

import LOGO_SRC from "../../Icons/Logo.svg";
import INFO_SRC from "../../Icons/Info.svg";
import USER_SRC from "../../Icons/user.svg";
import SYSTEM_SRC from "../../Icons/gear.svg";
import IMAGE_SRC from "../../Icons/image.svg";

import { useNavigate } from "react-router";
import {Tooltip} from "../../components/Tooltip";
import { getAppVersionLabel } from "../../utility/appVersion";

type CSMShopfloorHeaderProps = {
  onLogoContainerClick?: () => void;
};

// Bit flags persisted in UserDetails.Complexity
const MANAGER_BIT = 1 << 0;   // 1
const SHOPFLOOR_BIT = 1 << 1; // 2

const CSMShopfloorHeader: FunctionComponent<CSMShopfloorHeaderProps> = ({
  onLogoContainerClick,
}) => {
  const navigate = useNavigate();

  // --------- Store state needed for this header -----------
  const user = useStoreState((s) => s.user);
  const isManagerMode = useStoreState((s) => s.appSettings.isManagerMode);
  const setIsManagerMode = useStoreActions((a) => a.appSettings.setIsManagerMode);
  const setIsActiveJobMode = useStoreActions((a) => a.appSettings.setIsActiveJobMode);

  // Derive role access from user.info.complexity bits (fallback to user.complexity if needed)
  const { canSeeManager, canSeeShopfloor } = useMemo(() => {
    // Prefer nested info if present, else fallback
    const complexityRaw =
      (user as any)?.info?.complexity ??
      (user as any)?.complexity ??
      0;

    const complexity = Number(complexityRaw) || 0;

    const hasManager = (complexity & MANAGER_BIT) === MANAGER_BIT;
    const hasShop = (complexity & SHOPFLOOR_BIT) === SHOPFLOOR_BIT;

    // If you later expose explicit booleans, you could do:
    // const hasManager = !!((user as any)?.info?.manager ?? ((complexity & MANAGER_BIT) === MANAGER_BIT));
    // const hasShop = !!((user as any)?.info?.shopFloor ?? ((complexity & SHOPFLOOR_BIT) === SHOPFLOOR_BIT));

    return { canSeeManager: hasManager, canSeeShopfloor: hasShop };
  }, [user]);

  // --------- Handlers ----------------------
  const handleLogoClick = () => {
    if (onLogoContainerClick) onLogoContainerClick();
    else navigate("/home");
  };

  const goImage = () => navigate("/images");
  const goSystem = () => navigate("/system");
  const doLogout = () => navigate("/logout");

  // NEW: toggle + navigate (guarded by visibility)
  const handleManagerClick = () => {
    if (!canSeeManager) return;
    setIsManagerMode(true);
    setIsActiveJobMode(true);
    navigate("/home");
  };

  const handleShopFloorClick = () => {
    if (!canSeeShopfloor) return;
    setIsManagerMode(false);
    navigate("/home");
  };

  // Swap wrappers based on mode (keep your existing classes)
  const shopWrapperClass = isManagerMode
    ? "csm-manager-wrapper"
    : "csm-shop-floor-wrapper";

  const managerWrapperClass = isManagerMode
    ? "csm-shop-floor-wrapper"
    : "csm-manager-wrapper";

  // Optionally: if the current mode isn't permitted by the user's roles,
  // you can auto-correct the mode here. Commented to avoid side-effects:
  // useEffect(() => {
  //   if (isManagerMode && !canSeeManager && canSeeShopfloor) setIsManagerMode(false);
  //   if (!isManagerMode && !canSeeShopfloor && canSeeManager) setIsManagerMode(true);
  // }, [isManagerMode, canSeeManager, canSeeShopfloor, setIsManagerMode]);

  return (
    <div className="csm-header">
      {/* LEFT: Logo + app names */}
      <div className="logo-parent">
        <div
          className="logo"
          onClick={handleLogoClick}
          role="button"
          aria-label="Home"
          tabIndex={0}
        >
          <img className="logo-child" src={LOGO_SRC} alt="" />
        </div>

        <div className="frame-parent">
          <div
            style={{
              color: "#36424A",   // Figma; was "black"
              fontSize: 20,
              fontFamily: "Noto Sans",
              fontWeight: "300",
              // "33px", not 33 — see the matching note in CSMShopfloorHeader.tsx.
              // Unitless 33 is a 660px line box, which breaks the phone header
              // once it's allowed to size to its content.
              lineHeight: "33px",
              wordWrap: "break-word",
            }}
          >
            CSM
          </div>                  
        </div>
      </div>

      {/* RIGHT: utilities only (dropdowns removed) */}
      <div className="button-link-parent">
        
        {/* User name (click => logout) */}
        <div
          className="utility-link"
          onClick={doLogout}
          role="button"
          title="Log out"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && doLogout()}
        >
          <img className="user-icon" alt="" src={USER_SRC} />
          <div className="select-warehouse">
            {(user as any)?.info?.name ?? (user as any)?.fullName ?? "User"}
          </div>
        </div>

        {/* System link visible in Manager mode (unchanged) */}
        {isManagerMode && (
          <div
            className="utility-link"
            onClick={goSystem}
            role="button"
            title="Settings"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goSystem()}
          >
            <img className="user-icon csm-gear-icon" alt="" src={SYSTEM_SRC} />
            <div className="select-warehouse"></div>
          </div>
        )}

        {/* Info (about) */}
        <div className="info-wrapper" role="button" tabIndex={0} aria-label="About">
  

  {/* Version comes from the AppVersion Key Vault secret, not source. */}
  <Tooltip text={getAppVersionLabel()}>
    <img className="path-346-icon" alt="" src={INFO_SRC} />
  </Tooltip>
</div>
      </div>
    </div>
  );
};

export default CSMShopfloorHeader;